import { LineItem } from 'shared/types/cart';
import {
  PriceDetails,
  ProcessCheckoutPayload,
  PSP,
  UNKNOWN_PSP,
} from 'shared/types/checkout';
import { ProgramData } from 'shared/types/checkout/ProductApi';
import { sdk } from 'sdk';
import { getProductsFromLineItems, hasBundleId, sendGTMEvent } from 'utils';
import {
  COHESION_BUNDLE_MAPPER,
  CohesionBundleType,
  conversionEvents,
  ConversionTracked,
  Correlation,
} from 'utils/eventing';
import { readMarketplaceFxQuote } from 'utils/marketplace-fx-quote';
import { centsToDollars, sumMoney } from 'utils/price';
import { hyphenateForTagular } from 'utils/tagular';
import { normalizeUUID } from 'utils/uuid';
import {
  GetFinalizeStripeCheckoutPayload,
  GetProcessCheckoutPayload,
  GetValidateCheckoutPayloadForPSPOptions,
  ProcessCheckoutPayloadForPSP,
  SendOrderCompletedEventOptions,
  SendPaymentInfoEnteredEventOptions,
  ValidateCheckoutPayload,
  ValidatePayloadForPSP,
} from './types';

export type CohesionPaymentMethod = 'card' | 'paypal' | 'upi';

const CARD_PAYMENT_METHODS = new Set(['card', 'apple_pay', 'google_pay']);

export const toCohesionPaymentMethod = (
  paymentMethod?: string,
): CohesionPaymentMethod | undefined => {
  if (!paymentMethod) {
    return undefined;
  }

  const normalized = paymentMethod.toLowerCase();
  if (normalized === 'paypal' || normalized === 'upi') {
    return normalized;
  }

  if (CARD_PAYMENT_METHODS.has(normalized)) {
    return 'card';
  }

  return undefined;
};

const getBrandTextFromProgramId = async (
  programId: string,
): Promise<null | string> => {
  try {
    const response = await sdk.callAction<ProgramData>({
      actionName: 'productApi/getProgramInfo',
      payload: { programUuid: normalizeUUID(programId) },
    });

    if (response?.isError || !response?.data) {
      console.error(
        'Error fetching program:',
        programId,
        response.isError ? 'SDK Error' : 'No data',
      );
      return null;
    }

    // Get the partner's key (e.g., "HarvardX") from the first partner
    return response?.data?.partners?.[0]?.key ?? 'edX';
  } catch (error) {
    console.error('Error fetching program:', error);
    return null;
  }
};

export const getLineItemsWithBrandText = async (
  lineItems: LineItem[],
): Promise<LineItem[]> => {
  if (lineItems.length <= 0) {
    return lineItems;
  }

  /**
   * Get the first program ID for each bundle since all items should be from the same program
   *
   * @example
   * { bundle_1: "program_id_1", bundle_2: "program_id_2" }
   *
   */
  const bundleByPrograms = lineItems.reduce<Record<string, string>>(
    (acc, item) => {
      // early return if the bundle already has a program ID
      if (!item?.bundleId || acc[item?.bundleId]) {
        return acc;
      }

      const programId =
        item?.bundleId ??
        item?.variant?.attributes?.['ref-edx-course-entitlement'] ??
        item.productId;

      return programId ? { ...acc, [item.bundleId]: programId } : acc;
    },
    {},
  );

  // Get the brand name for each program
  const brandTextByPrograms = (
    await Promise.allSettled(
      Object.entries(bundleByPrograms).map(async ([key, value]) => {
        const brand = await getBrandTextFromProgramId(value);

        return { key, brand };
      }),
    )
  ).reduce<Record<string, string | null>>((acc, result) => {
    // get only the successful responses
    if (result.status === 'fulfilled' && result?.value?.brand) {
      return { ...acc, [result.value.key]: result.value.brand };
    }

    return acc;
  }, {});

  // early return if all the requests failed or have no data
  if (Object.keys(brandTextByPrograms).length <= 0) {
    return lineItems;
  }

  return lineItems.map((item) => {
    const bundleId = item?.bundleId;

    if (bundleId && brandTextByPrograms[bundleId]) {
      return {
        ...item,
        variant: item.variant && {
          ...item.variant,
          attributes: {
            ...item.variant.attributes,
            'brand-text': brandTextByPrograms[bundleId],
          },
        },
      };
    }

    return item;
  });
};

export const getValidateCheckoutPayloadForPSP = <T extends PSP>({
  cartID,
  priceDetails,
  psp,
  pspData,
  selectedCurrency,
  showUpi,
}: GetValidateCheckoutPayloadForPSPOptions<T>): ValidateCheckoutPayload<T> => {
  switch (psp) {
    case PSP.STRIPE: {
      const marketplaceFxQuote = readMarketplaceFxQuote();
      return {
        actionName: `${psp}/validateStripeCheckout`,
        payload: {
          cartID,
          amount: priceDetails.cartTotal ?? 0,
          paymentMethodID: pspData,
          selectedCurrency,
          showUpi: showUpi === true,
          ...(marketplaceFxQuote && { marketplaceFxQuote }),
        } as ValidatePayloadForPSP[T],
      };
    }
    case PSP.PAYPAL:
      return {
        actionName: `${psp}/validatePayPalCheckout`,
        payload: pspData as ValidatePayloadForPSP[T],
      };
    default:
      return {
        actionName: `${psp}/validateStripeCheckout`,
        payload: pspData as ValidatePayloadForPSP[T],
      };
  }
};

export const getProcessCheckoutPayloadForPSP = ({
  ctPaymentID,
  cartID,
  payerName,
  psp,
}: GetProcessCheckoutPayload): ProcessCheckoutPayloadForPSP => {
  const payload = {
    ctPaymentID,
    payerName,
    cartID,
  } as ProcessCheckoutPayload;

  switch (psp) {
    case PSP.STRIPE:
      return {
        actionName: `${psp}/processStripeCheckout`,
        payload,
      };
    case PSP.PAYPAL:
      return {
        actionName: `${psp}/capturePayPalOrder`,
        payload,
      };
    default:
      return {
        actionName: `${psp as string}/processCheckout`,
        payload,
      };
  }
};

export const getFinalizeStripeCheckoutPayload = ({
  paymentIntentId,
  resumeToken,
  ctPaymentID,
  cartID,
}: GetFinalizeStripeCheckoutPayload) => ({
  actionName: `${PSP.STRIPE}/finalizeStripeCheckout`,
  payload: {
    paymentIntentId,
    resumeToken,
    ...(ctPaymentID ? { ctPaymentID } : {}),
    ...(cartID ? { cartID } : {}),
  },
});

export const getReleaseStripeCheckoutAfterFailurePayload = ({
  paymentIntentId,
  resumeToken,
}: {
  paymentIntentId: string;
  resumeToken: string;
}) => ({
  actionName: `${PSP.STRIPE}/releaseStripeCheckoutAfterFailure`,
  payload: {
    paymentIntentId,
    resumeToken,
  },
});

export const buildPaymentReturnUrl = (
  origin: string,
  locale: string,
): string => {
  const normalizedLocale = locale.replace(/^\/+|\/+$/g, '') || 'en';
  return `${origin.replace(/\/+$/, '')}/${normalizedLocale}/payment-return`;
};

export const sendPaymentInfoEnteredEvent = ({
  cartID,
  priceDetails,
  paymentMethodType,
  lineItems,
}: SendPaymentInfoEnteredEventOptions) => {
  sendGTMEvent({
    event: 'Payment Info Entered',
    properties: {
      track_plan_id: 31,
      trigger_source: 'client-side',
      cart_id: cartID,
      checkout_id: cartID,
      currency: priceDetails.currencyCode ?? 'USD',
      revenue: centsToDollars(priceDetails.lineItemsTaxed.amount),
      payment_method: paymentMethodType,
      discount: priceDetails.discountInDollars,
      products: getProductsFromLineItems(lineItems),
    },
  });
};

export const sendOrderCompletedEvent = <T extends PSP | UNKNOWN_PSP>({
  psp,
  cartID,
  priceDetails,
  paymentMethodType,
  order: {
    orderId,
    taxed,
    discountCodes,
    discountOnLineItems,
    discountOnTotalPrice,
    lineItems,
  },
}: SendOrderCompletedEventOptions<T>) => {
  sendGTMEvent({
    event: 'Order Completed',
    properties: {
      track_plan_id: 32,
      trigger_source: 'client-side',
      order_id: orderId,
      checkout_id: cartID,
      total: centsToDollars(taxed?.grossAmount),
      revenue: centsToDollars(priceDetails.lineItemsTaxed.amount),
      currency: priceDetails.currencyCode ?? 'USD',
      tax: centsToDollars(taxed?.totalTax),
      coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
      coupon_name: discountCodes
        .slice(0, -1)
        .map(({ code }) => code)
        .filter(Boolean),
      discount: centsToDollars(
        sumMoney(discountOnTotalPrice, discountOnLineItems),
      ),
      processor_name: psp,
      payment_method: paymentMethodType,
      products: getProductsFromLineItems(lineItems),
    },
  });
};

export const sendCohesionConversionEvent = (
  lineItems: LineItem[],
  category: string,
  action: string,
  priceDetails?: PriceDetails,
  discountCode?: string,
  orderNumber?: string,
  selectedCurrency?: string,
  paymentMethod?: string,
) => {
  const isBundle = hasBundleId(lineItems);

  // Calculate overall discount percentage from priceDetails if available
  const overallDiscountPercentage =
    priceDetails?.discountInDollars &&
    priceDetails?.cartTotal &&
    priceDetails.discountInDollars > 0
      ? (() => {
          const discountInCents = priceDetails.discountInDollars * 100;
          const originalPriceInCents = priceDetails.cartTotal + discountInCents;
          const percentage =
            originalPriceInCents > 0
              ? Math.round((discountInCents / originalPriceInCents) * 100)
              : 0;
          return percentage;
        })()
      : null;

  const products = lineItems.map((lineItem) => {
    const productId =
      lineItem.productType?.obj?.key === 'edx_course_entitlement'
        ? lineItem.variant?.attributes?.['course-key']
        : lineItem.productKey;

    const productType =
      (lineItem.variant?.attributes
        ?.programProductTypeKey as CohesionBundleType) ?? 'certificate';

    const courseVariant: string | undefined = isBundle
      ? COHESION_BUNDLE_MAPPER[productType]
      : 'courses';

    // Check for line item specific discount
    const lineItemDiscountPercentage = lineItem.discounts?.[0]?.discountedAmount
      ?.centAmount
      ? Math.round(
          (lineItem.discounts[0].discountedAmount.centAmount /
            (lineItem.price?.centAmount ?? 1)) *
            100,
        )
      : null;

    const product: {
      id: string;
      brand: string;
      externalId: string;
      category: string;
      variant: string | undefined;
      name: string;
      price: number;
      currency?: string;
      characteristics?: string[];
    } = {
      id: productId ?? '',
      brand: hyphenateForTagular(
        lineItem.variant?.attributes?.['brand-text'] ?? '',
      ),
      externalId: lineItem.variant?.attributes?.['external-ids-variant'] ?? '',
      category: lineItem.variant?.attributes?.['primary-subject-area'] ?? '',
      variant: courseVariant,
      name: lineItem.name,
      price: lineItem.price?.centAmount ?? 0,
    };

    // Add currency if provided
    if (selectedCurrency) {
      product.currency = selectedCurrency;
    }

    // Build characteristics array to include ALL applicable discounts
    const characteristics: string[] = [];

    if (lineItemDiscountPercentage) {
      characteristics.push(`${lineItemDiscountPercentage}% product discount`);
    }

    if (overallDiscountPercentage) {
      characteristics.push(`${overallDiscountPercentage}% cart discount`);
    }

    if (characteristics.length > 0) {
      product.characteristics = characteristics;
    }

    return product;
  });

  const cohesionPaymentMethod = toCohesionPaymentMethod(paymentMethod);

  const tagularElement: ConversionTracked['metadata'] = {
    category,
    action,
    timestamp: new Date().toISOString(),
    productList: products,
    ...(cohesionPaymentMethod ? { paymentMethod: cohesionPaymentMethod } : {}),
  };

  const correlationData = orderNumber
    ? {
        correlation: {
          transactionId: orderNumber,
        } as Correlation,
      }
    : {};

  const { conversionTracked } = conversionEvents({
    ...correlationData,
    metadata: tagularElement,
  });

  // Include revenue if priceDetails is provided
  if (priceDetails) {
    conversionTracked({
      revenue: {
        estimatedRevenue: {
          amount: priceDetails.cartTotal ?? 0,
          code: discountCode ?? undefined,
          scale: priceDetails.discountInDollars
            ? Math.round(priceDetails.discountInDollars * 100)
            : 0,
        },
      },
    });
  } else {
    conversionTracked();
  }
};
